import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import componentBase from '../../../../../../src/lib/component-base';
import { dateFormatString } from '../../../../../../src/lib/date-time-format';
import { countryCodes } from '../../../../../../shared/lib/country-codes-iso-alpha-2';
import IconHeader from '../../../../../../shared/components/IconHeader/IconHeader';
import RecapIcon from '../../../../../../static/images/Recap-Icon-WHITE.svg';
import { translateEnumTypeToExportType } from '../../../../redux/selectors';

const has = Object.prototype.hasOwnProperty;

const dataExportRegions = {
  north_america: 'North America',
  oceania: 'Oceania',
  africa: 'Africa',
  latin_america: 'Latin America',
  europe: 'Europe',
  antarctica: 'Antarctica',
  asia: 'Asia',
  world: 'World',
};

@componentBase('DataExportQueryHistory')
export default class DataExportQueryHistory extends Component {
  static propTypes = {
    quoteData: PropTypes.object,
  }

  handleCountryNames = locations => countryCodes.filter(country =>
    locations.includes(country.Code))
    .map(country => country.Name);

  handleRegionNames = locations => locations.map(location =>
    dataExportRegions[location] || '');

  get queryData() {
    if (this.props && this.props.quoteData) {
      const { query } = this.props.quoteData;
      if (query) {
        return query;
      }
    }
    return null;
  }

  get exportType() {
    if (this.props && this.props.quoteData) {
      const { type } = this.props.quoteData;
      return translateEnumTypeToExportType(type);
    }
    return null;
  }

  get queries() {
    if (this.queryData) {
      const { queries } = this.queryData;
      if (queries) {
        return queries;
      }
    }
    return null;
  }

  get airlinesText() {
    if (this.queries) {
      const { airlines } = this.queries;
      if (airlines) {
        // add a space
        return airlines.replace(/[,]/g, ', ');
      }
    }
    return 'None Entered';
  }

  get arrivalLocations() {
    let text = '';
    if (this.queryData) {
      const { arrivals, arrivalType } = this.queryData;
      if (arrivals && arrivals.length && this.exportType !== 'departing') {
        const arrivalsArray = [];
        switch (arrivalType) {
          case 'country':
            arrivalsArray.push(...this.handleCountryNames(arrivals));
            break;
          case 'region':
            arrivalsArray.push(...this.handleRegionNames(arrivals));
            break;
          default:
            arrivalsArray.push(...arrivals);
        }
        text = arrivalsArray.join(', ');
      }
    }
    return text;
  }

  get departureLocations() {
    let text = '';
    if (this.queryData) {
      const { departures, departureType } = this.queryData;
      if ((departures && departures.length) &&
        (['arriving', 'deparr'].indexOf(this.exportType) === -1)) {
        const departuresArray = [];
        switch (departureType) {
          case 'country':
            departuresArray.push(...this.handleCountryNames(departures));
            break;
          case 'region':
            departuresArray.push(...this.handleRegionNames(departures));
            break;
          default:
            departuresArray.push(...departures);
        }
        text = departuresArray.join(', ');
      }
    }
    return text;
  }

  get arrivalTypeTexts() {
    if (this.queryData) {
      const { arrivalType } = this.queryData;
      if (this.exportType === 'departing') {
        return '';
      }

      let arrivalPrefix = 'Arrival';
      if (this.exportType === 'deparr') {
        arrivalPrefix = 'Departing or Arriving';
      }
      switch (arrivalType) {
        case 'region':
          return `${arrivalPrefix} Region(s)`;
        case 'country':
          return `${arrivalPrefix} Country/Countries`;
        default:
          return `${arrivalPrefix} Airport(s)`;
      }
    }
    return 'Arrival Airport(s)';
  }

  get departureTypeTexts() {
    if (this.queryData) {
      const { departureType } = this.queryData;

      if (this.exportType === 'arriving' || this.exportType === 'deparr') {
        return '';
      }
      switch (departureType) {
        case 'region':
          return 'Departure Region(s)';
        case 'country':
          return 'Departure Country/Countries';
        default:
          return 'Departure Airport(s)';
      }
    }
    return 'Departure Airport(s)';
  }

  get codesharesText() {
    let text = '';
    if (this.queries) {
      text = this.queries.includeCodeshares ? 'Yes' : 'No';
    }
    return text;
  }

  get cargosText() {
    let text = '';
    if (this.queries) {
      text = this.queries.includeCargo ? 'Yes' : 'No';
    }
    return text;
  }

  get dateRange() {
    const noDate = '';
    if (this.queryData) {
      const necessaryDateKeys = ['fromYear',
        'fromDay',
        'fromMonth',
        'toYear',
        'toDay',
        'toMonth'];
      if (!necessaryDateKeys.every(key => has.call(this.queryData, key))) {
        return noDate;
      }
      const {
        fromYear,
        fromDay,
        fromMonth,
        toYear,
        toDay,
        toMonth,
      } = this.queryData;
      const zeroedToMonth = toMonth < 10 ? `0${toMonth}` : toMonth;
      const zeroedFromMonth = fromMonth < 10 ? `0${fromMonth}` : fromMonth;
      const zeroedToDay = toDay < 10 ? `0${toDay}` : toDay;
      const zeroedFromDay = fromDay < 10 ? `0${fromDay}` : fromDay;
      const dateFormatClue = 'YYYY-MM-DD';
      const fromDate = moment.utc(`${fromYear}-${zeroedFromMonth}-${zeroedFromDay}`, dateFormatClue).format(dateFormatString(this.context.currentUser));
      const toDate = moment.utc(`${toYear}-${zeroedToMonth}-${zeroedToDay}`, dateFormatClue).format(dateFormatString(this.context.currentUser));
      return `${fromDate} through ${toDate}`;
    }
    return noDate;
  }

  render() {
    return (<div className='data-export-recap-container'>
      <div
        className='recap-header'
      >
        <IconHeader
          title='Data Export Recap'
          icon={RecapIcon}
        />
      </div>
      <div className='row'>
        <div className='col-xs-12 col-md-5'>
          <div
            className='recap-title-pack '
          >
            <h5
              className='recap-title'
            >
                  Date Range
            </h5>
            <h5>{this.dateRange}</h5>
          </div>
          <div
            className='recap-title-pack '
          >
            <h5
              className='recap-title'
            >
                  Include Codeshares?
            </h5>
            <h5>{this.codesharesText}</h5>
          </div>
          <div
            className='recap-title-pack '
          >
            <h5
              className='recap-title'
            >
                  Include Cargo Freighters?
            </h5>
            <h5>{this.cargosText}</h5>
          </div>
        </div>
        <div className='col-xs-12 col-md-7'>
          {this.departureTypeTexts ? <div
            className='recap-title-pack'
          >
            <h5
              className='recap-title'
            >
              {this.departureTypeTexts}
            </h5>
            <h5>{this.departureLocations}</h5>
          </div> : null}
          {this.arrivalTypeTexts ? <div
            className='recap-title-pack'
          >
            <h5
              className='recap-title'
            >
              {this.arrivalTypeTexts}
            </h5>
            <h5>{this.arrivalLocations}</h5>
          </div> : null}
          <div
            className='recap-title-pack'
          >
            <h5
              className='recap-title'
            >
                  Airline(s)
            </h5>
            <h5>{this.airlinesText}</h5>
          </div>
        </div>
      </div>
    </div>);
  }
}



// WEBPACK FOOTER //
// ./modules/DataExports/components/DataExportsHistoryDetail/children/DataExportQueryHistory/DataExportQueryHistory.js